import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { Loader2, Layers, TrendingUp, TrendingDown, ArrowRight, Zap, Droplets, Wrench, Fuel } from "lucide-react";

// Brand-aligned colors for each Utility member (Salary uses dept colors; same idea here).
// Picked for legibility on white + good contrast in stacked bars.
const MEMBER_COLOR = {
  "Electricity":          "#FFA500",  // orange (highest impact)
  "Diesel":               "#FB7185",  // rose (fuel)
  "Water":                "#20B2AA",  // teal
  "Water Tanker":         "#0E7490",  // cyan-dark
  "Rent - DG":            "#A78BFA",  // violet
  "Repairs & Maint - DG": "#6366F1",  // indigo
  // R&M group
  "Repairs & Maintenance": "#FB7185",
  // HK & Laundry group
  "Housekeeping Charges": "#20B2AA",
  "Guest Laundry":        "#A78BFA",
  "Hostel Laundry":       "#6366F1",
  // Others group
  "Freight & Transport":  "#FFA500",
  "Room Amenities":       "#20B2AA",
  "Local Conveyance":     "#A78BFA",
  "Local Event":          "#6366F1",
  "Pet Expense":          "#FB7185",
  "Legal & Professional": "#0E7490",
  "Software":             "#9370DB",
  "BD Photography":       "#FFCC00",
  "Horticulture":         "#10B981",
  "Communication":        "#94a3b8",
};

const MEMBER_ICON = {
  "Electricity":          Zap,
  "Diesel":               Fuel,
  "Water":                Droplets,
  "Water Tanker":         Droplets,
  "Rent - DG":            Wrench,
  "Repairs & Maint - DG": Wrench,
};

const fmtINR = (v) => v == null || isNaN(v) ? "—" : "₹" + Math.round(v).toLocaleString("en-IN");
const fmtL = (v) => v == null || isNaN(v) ? "—" : "₹" + (Number(v) / 100000).toFixed(2) + "L";
const fmtPct = (v) => v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`;

const lifecycleClasses = (tone) => ({
  indigo:  "text-indigo-800 bg-indigo-100 border-indigo-300",
  amber:   "text-amber-800 bg-amber-100 border-amber-300",
  rose:    "text-rose-800 bg-rose-100 border-rose-300",
  emerald: "text-emerald-800 bg-emerald-100 border-emerald-300",
}[tone] || "text-zinc-700 bg-zinc-100 border-zinc-200");

export default function PropertyMembersDialog({ open, onClose, target, category, monthLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fy, setFy] = useState("recent"); // "recent" = last 12 mo; else "FY 24-25" etc.

  useEffect(() => {
    if (!open || !target) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        params.set("category", category);
        if (fy && fy !== "recent") {
          params.set("fy", fy);
        } else {
          params.set("months", "12");
        }
        if (target.locations.length === 1) params.set("location", target.locations[0]);
        else params.set("locations", target.locations.join(","));
        const { data } = await api.get(`/mis-analysis/property-members?${params.toString()}`);
        if (!cancelled) setData(data);
      } catch (e) {
        logError("property-members fetch failed", e);
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, target, category, fy]);

  // Reset FY filter when the dialog re-opens for a different target
  useEffect(() => {
    if (open && target) setFy("recent");
  }, [open, target]);

  if (!open) return null;
  const isCluster = (target?.locations?.length || 0) > 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[1280px] max-h-[90vh] overflow-y-auto"
        data-testid="property-members-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black text-lg flex items-center gap-2 flex-wrap">
            {target?.name}
            {isCluster && (
              <span className="text-[10px] uppercase tracking-wider font-bold border border-indigo-300 bg-indigo-50 text-indigo-800 px-2 py-0.5 rounded">
                Cluster · {target.locations.length} props
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider font-bold border border-zinc-200 bg-zinc-50 text-zinc-700 px-2 py-0.5 rounded">
              {category} · {fy === "recent" ? "last 12 months" : fy}
            </span>
          </DialogTitle>
          {/* FY filter chips */}
          {data?.available_fys?.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-2" data-testid="property-members-fy-filter">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">Period</span>
              <FyChip active={fy === "recent"} label="Last 12 mo" onClick={() => setFy("recent")} testid="property-members-fy-recent" />
              {data.available_fys.map((f) => (
                <FyChip key={f} active={fy === f} label={f} onClick={() => setFy(f)} testid={`property-members-fy-${f.replace(/\W/g, "")}`} />
              ))}
            </div>
          )}
        </DialogHeader>

        {loading && (
          <div className="py-16 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> loading {category.toLowerCase()} history…
          </div>
        )}

        {!loading && data && data.rows?.length === 0 && (
          <div className="py-16 text-center text-zinc-500 text-[11px]">
            No {category.toLowerCase()} data for the selected period.
          </div>
        )}

        {!loading && data && data.rows?.length > 0 && (
          <div className="space-y-4">
            {/* Summary KPIs */}
            <SummaryStrip data={data} />

            {/* Communication benchmark strip — only present for Others group */}
            {data.comm_benchmark_meta && <CommBenchmarkStripDialog meta={data.comm_benchmark_meta} rows={data.rows} />}

            {/* Locations strip (cluster view) */}
            {isCluster && data.locations?.length > 1 && (
              <section className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Properties in this group · latest month</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {data.locations.map((l) => (
                    <div key={l.location} className="bg-white border border-zinc-200 rounded p-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-bold text-[11.5px] text-zinc-800">{l.location}</span>
                        {l.lifecycle_status && l.lifecycle_status !== "operational" && (
                          <span className={`text-[8.5px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded ${lifecycleClasses(l.lifecycle_tone)}`}>
                            {l.lifecycle_label}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500">{l.type} · {l.model}</div>
                      <div className="mt-1 tabular-nums text-[11px] text-zinc-700">
                        {category}: <span className="font-bold">{fmtL(l.latest_total)}</span> · Rev <span className="text-zinc-500">{fmtL(l.latest_revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Member-mix bar chart (12-month stacked) */}
            <MemberStackedChart data={data} category={category} />

            {/* 12-month × member matrix table */}
            <MemberHistoryTable data={data} category={category} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryStrip({ data }) {
  const s = data.summary || {};
  const top = s.top_member;
  const topVal = top ? (s.member_totals || {})[top] : 0;
  const topShare = s.actual_total ? Math.round(topVal / s.actual_total * 100) : 0;
  const periodLabel = data.fy_selected ? data.fy_selected : `last ${data.rows?.length || 12} months`;
  const varColor = s.variance_pct == null ? "border-zinc-200 text-zinc-600" :
                   s.variance_pct > 15 ? "border-rose-300 text-rose-800" :
                   s.variance_pct > 5  ? "border-amber-300 text-amber-800" :
                   s.variance_pct < -5 ? "border-emerald-300 text-emerald-800" :
                   "border-zinc-200 text-zinc-700";
  return (
    <section className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <KPI label={`${data.label} actual · ${periodLabel}`} value={fmtL(s.actual_total)} sub={fmtINR(s.actual_total)} accent="#20B2AA" />
      <KPI label={`AOP plan · ${periodLabel}`} value={s.plan_total ? fmtL(s.plan_total) : "—"} sub={s.plan_total ? "FY 26-27" : "no plan in window"} accent="#6366F1" />
      <div className={`bg-white border-2 rounded-md p-2.5 ${varColor}`}>
        <div className="text-[9.5px] uppercase font-bold tracking-wider opacity-80">Variance vs AOP</div>
        <div className="font-display font-black tabular-nums mt-1" style={{ fontSize: 17, lineHeight: 1.4, paddingBottom: 2 }}>
          {s.variance_pct == null ? "—" : `${s.variance_pct >= 0 ? "+" : ""}${s.variance_pct}%`}
        </div>
        <div className="text-[10px] tabular-nums opacity-80">{s.variance == null ? "—" : `${s.variance >= 0 ? "+" : ""}${fmtL(s.variance)}`}</div>
      </div>
      <KPI label="Top line item" value={top || "—"} sub={top ? `${fmtL(topVal)} · ${topShare}% of group` : "—"} accent="#FFA500" />
      <KPI label="# Line items" value={String(data.members.length)} sub={data.members.slice(0, 3).join(" · ") + (data.members.length > 3 ? "…" : "")} accent="#FB7185" />
    </section>
  );
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className="bg-zinc-50/60 border border-zinc-200 rounded-md p-2.5">
      <div className="text-[9.5px] uppercase font-bold tracking-wider text-zinc-500">{label}</div>
      <div className="font-display font-black tabular-nums mt-1 text-zinc-900" style={{ fontSize: 17, lineHeight: 1.4, paddingBottom: 2 }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5 tabular-nums overflow-hidden text-ellipsis whitespace-nowrap">{sub}</div>}
      <div className="mt-1 h-[2px] w-5 rounded-full" style={{ background: accent }} />
    </div>
  );
}

function MemberStackedChart({ data, category }) {
  if (!data.rows?.length) return null;
  const maxV = Math.max(1, ...data.rows.map((r) => r.total));
  const hasRollupOnly = data.rows.some((r) => r.is_rollup_only);
  const periodLabel = data.fy_selected ? data.fy_selected : `last ${data.rows.length} months`;
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="members-dialog-trend">
      <header className="mb-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{data.rows.length}-month trend · stacked by line item</div>
        <h3 className="font-display font-black text-[15px]">{category} · {periodLabel}</h3>
        {hasRollupOnly && (
          <div className="text-[10.5px] text-zinc-500 mt-0.5 italic">
            Months in zinc/striped = only the rolled-up "Utilities Expense" was tracked (pre-Sep 2025) — no line-item breakdown.
          </div>
        )}
      </header>
      <div className="relative">
      {/* Row 1: Bar value labels — fixed at the top so they never collide with occupancy dots */}
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${data.rows.length}, minmax(0, 1fr))` }}>
        {data.rows.map((r) => (
          <div key={`v-${r.key}`} className="text-center text-[9px] text-zinc-500 tabular-nums leading-none mb-1.5 truncate">{fmtL(r.total)}</div>
        ))}
      </div>
      {/* Row 2: Bars + occupancy overlay */}
      <div className="grid gap-1.5 items-end relative" style={{ gridTemplateColumns: `repeat(${data.rows.length}, minmax(0, 1fr))`, height: 84 }}>
        {data.rows.map((r, i) => {
          const h = Math.max(6, Math.round(r.total / maxV * 80));
          // Rollup-only month — render as a single zinc bar with diagonal stripe pattern
          if (r.is_rollup_only) {
            return (
              <div key={r.key} className="w-full rounded-t relative" style={{ height: h, background: "#94a3b8" }}
                   data-testid={`members-trend-${i}`}
                   title={`${fmtL(r.total)} · total only (no breakdown)`}>
                <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 6px)" }} />
              </div>
            );
          }
          const segs = data.members.map((m) => [m, (r.members || {})[m] || 0]).filter(([, v]) => v > 0);
          return (
            <div key={r.key} className="w-full rounded-t flex flex-col-reverse overflow-hidden"
                 style={{ height: h, background: "#f4f4f5" }}
                 data-testid={`members-trend-${i}`}>
              {segs.map(([m, v]) => (
                <div key={m}
                     title={`${m}: ${fmtL(v)} (${Math.round(v / r.total * 100)}%)`}
                     style={{ flexBasis: `${v / r.total * 100}%`, background: MEMBER_COLOR[m] || "#94a3b8" }} />
              ))}
            </div>
          );
        })}
        {/* Occupancy line overlay sits inside the bar zone */}
        {data.is_variable && <DialogOccupancyOverlay rows={data.rows} />}
      </div>
      {/* Row 3: Month labels — fixed at the bottom */}
      <div className="grid gap-1.5 mt-1.5" style={{ gridTemplateColumns: `repeat(${data.rows.length}, minmax(0, 1fr))` }}>
        {data.rows.map((r) => (
          <div key={`m-${r.key}`} className="text-center text-[9px] text-zinc-500">{r.month_label.replace(/\s\d{4}$/, "").slice(0, 3)}</div>
        ))}
      </div>
      </div>
      {/* Legend */}
      <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px] text-zinc-600">
        {data.members.map((m) => {
          const Icon = MEMBER_ICON[m];
          return (
            <div key={m} className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: MEMBER_COLOR[m] || "#94a3b8" }} />
              {Icon && <Icon className="w-3 h-3 text-zinc-500" />}
              <span className="font-medium">{m}</span>
              <span className="tabular-nums text-zinc-500">{fmtL((data.summary?.member_totals || {})[m])}</span>
            </div>
          );
        })}
        {hasRollupOnly && (
          <div className="flex items-center gap-1.5 border-l border-zinc-200 pl-3 ml-1" title="Pre-Sep 2025 — only rolled-up total was tracked">
            <span className="inline-block w-2.5 h-2.5 rounded-sm relative overflow-hidden" style={{ background: "#94a3b8" }}>
              <span className="absolute inset-0 opacity-40" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 1.5px, rgba(255,255,255,0.6) 1.5px, rgba(255,255,255,0.6) 3px)" }} />
            </span>
            <span className="font-medium italic">Total only · pre-Sep 2025</span>
            <span className="tabular-nums text-zinc-500">{fmtL(data.summary?.rollup_only_total || 0)}</span>
          </div>
        )}
        {/* Occupancy line legend chip — visible whenever any month carries an occ_pct value */}
        {data.is_variable && data.rows?.some((r) => r.occ_pct != null) && (
          <div className="flex items-center gap-1.5 border-l border-zinc-200 pl-3 ml-1" title="Property/cluster occupancy: Σ sold beds / Σ available beds across the window">
            <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-teal-700 ring-2 ring-white" />
            <span className="font-medium text-teal-800">Occ %</span>
          </div>
        )}
      </div>
    </section>
  );
}

function MemberHistoryTable({ data, category }) {
  const rows = (data.rows || []).slice().reverse(); // newest first
  const isOthers = category === "Others";
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-4">
      <header className="mb-3 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Detailed history · {category}</div>
          <h3 className="font-display font-black text-[15px]">Each month broken down by line item</h3>
        </div>
        <Layers className="w-4 h-4 text-zinc-400" />
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-[11.5px] table-auto border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr className="text-left text-[9.5px] uppercase tracking-wider text-zinc-500">
              <th className="py-2 pr-2 sticky left-0 bg-white z-10 border-b border-zinc-200 min-w-[110px]">Month</th>
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">Total</th>
              {data.members.map((m) => (
                <th key={m} className="py-2 px-1.5 text-right border-b border-zinc-200">
                  <div className="flex items-center justify-end gap-1">
                    <span className="inline-block w-2 h-2 rounded-sm" style={{ background: MEMBER_COLOR[m] || "#94a3b8" }} />
                    {m.replace(" - ", "-")}
                  </div>
                </th>
              ))}
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">AOP plan</th>
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">vs AOP</th>
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">Occ%</th>
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">% of Rev</th>
              {isOthers && <th className="py-2 px-1.5 text-right border-b border-zinc-200" title="Communication actual vs ₹6.5/bed/day benchmark">Comm vs ₹6.5</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={data.members.length + 6 + (isOthers ? 1 : 0)} className="py-6 text-center text-zinc-400">No data for this window.</td></tr>
            ) : rows.map((r, i) => {
              const prev = rows[i + 1];
              const momPct = prev?.total ? Math.round((r.total - prev.total) / prev.total * 1000) / 10 : null;
              const t = momTone(momPct);
              const Icon = t.icon;
              return (
                <tr key={r.key} className={`hover:bg-zinc-50 ${r.is_rollup_only ? "bg-zinc-50/40" : ""}`} data-testid={`members-dialog-row-${r.key}`}>
                  <td className={`py-2 pr-2 border-b border-zinc-100 font-bold text-zinc-800 sticky left-0 ${r.is_rollup_only ? "bg-zinc-50" : "bg-white"}`}>
                    <div className="flex items-center gap-2">
                      <span>{r.month_label}</span>
                      <span className="text-[9px] text-zinc-400 uppercase tracking-wider">{r.fy}</span>
                      {r.is_rollup_only && (
                        <span className="text-[8.5px] uppercase tracking-wider font-bold border border-zinc-300 bg-white text-zinc-600 px-1.5 py-0.5 rounded italic" title="Only rolled-up Utilities was tracked — no breakdown captured">
                          Total only
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-1.5 text-right border-b border-zinc-100">
                    <div className="font-bold tabular-nums text-zinc-900">{fmtL(r.total)}</div>
                    {momPct != null && (
                      <div className={`inline-flex items-center gap-0.5 ${t.text} tabular-nums text-[9.5px] font-bold`}>
                        <Icon className="w-2.5 h-2.5" />
                        {momPct >= 0 ? "+" : ""}{momPct}%
                      </div>
                    )}
                  </td>
                  {data.members.map((m) => {
                    const v = (r.members || {})[m] || 0;
                    const share = r.total ? Math.round(v / r.total * 100) : 0;
                    if (r.is_rollup_only) {
                      return (
                        <td key={m} className="py-2 px-1.5 text-right tabular-nums border-b border-zinc-100">
                          <div className="text-zinc-300" title="No breakdown captured before Sep 2025">—</div>
                        </td>
                      );
                    }
                    return (
                      <td key={m} className="py-2 px-1.5 text-right tabular-nums border-b border-zinc-100">
                        <div className={v ? "text-zinc-700" : "text-zinc-300"}>{v ? fmtL(v) : "—"}</div>
                        {v > 0 && <div className="text-[9px] text-zinc-400 tabular-nums">{share}%</div>}
                      </td>
                    );
                  })}
                  <td className="py-2 px-1.5 text-right tabular-nums border-b border-zinc-100 text-zinc-600">{r.plan ? fmtL(r.plan) : "—"}</td>
                  <td className="py-2 px-1.5 text-right border-b border-zinc-100">
                    {r.plan ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded border tabular-nums font-bold text-[10.5px] ${varianceTone(r.variance_pct)}`}>
                        {r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%
                      </span>
                    ) : <span className="text-[10.5px] text-zinc-400">—</span>}
                  </td>
                  <td className="py-2 px-1.5 text-right tabular-nums text-zinc-600 border-b border-zinc-100">{r.occ_pct == null ? "—" : fmtPct(r.occ_pct)}</td>
                  <td className="py-2 px-1.5 text-right tabular-nums text-zinc-600 border-b border-zinc-100">{r.share_of_revenue_pct == null ? "—" : fmtPct(r.share_of_revenue_pct)}</td>
                  {isOthers && (
                    <td className="py-2 px-1.5 text-right border-b border-zinc-100">
                      {/* Hide variance pill for pre-tracking months. Communication line-item
                          tracking started Oct/Nov 2025; before that, actual was 0 which makes
                          variance always −100% — meaningless. Use ₹1L as a "tracking is live"
                          threshold (network-wide Communication is ~₹5L/mo). */}
                      {r.comm_variance_pct != null && r.comm_actual >= 100000 ? (
                        <span className={`inline-block px-1.5 py-0.5 rounded border tabular-nums font-bold text-[10.5px] ${
                          r.comm_variance_pct > 15 ? "text-rose-800 bg-rose-50 border-rose-200" :
                          r.comm_variance_pct > 5 ? "text-amber-800 bg-amber-50 border-amber-200" :
                          r.comm_variance_pct < -15 ? "text-zinc-500 bg-zinc-50 border-zinc-200" :
                          "text-emerald-800 bg-emerald-50 border-emerald-200"
                        }`}
                              title={`Comm ₹${(r.comm_actual||0).toLocaleString("en-IN")} vs benchmark ₹${(r.comm_benchmark||0).toLocaleString("en-IN")} (${r.days_in_month || 30} days × ${r.available_beds ? Math.round(r.available_beds) : "?"} beds × ₹6.5)`}>
                          {r.comm_variance_pct > 0 ? "+" : ""}{r.comm_variance_pct}%
                        </span>
                      ) : (
                        <span className="text-[10.5px] text-zinc-400" title="Communication line-item tracking started late 2025 · no data captured for this month">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function momTone(pct) {
  if (pct == null) return { text: "text-zinc-500", icon: ArrowRight };
  if (pct >= 25) return { text: "text-rose-700", icon: TrendingUp };
  if (pct >= 10) return { text: "text-amber-700", icon: TrendingUp };
  if (pct <= -10) return { text: "text-emerald-700", icon: TrendingDown };
  return { text: "text-zinc-600", icon: ArrowRight };
}

function varianceTone(pct) {
  if (pct == null) return "text-zinc-500 bg-zinc-50 border-zinc-200";
  if (pct > 15) return "text-rose-800 bg-rose-50 border-rose-200";
  if (pct > 5) return "text-amber-800 bg-amber-50 border-amber-200";
  if (pct < -5) return "text-emerald-800 bg-emerald-50 border-emerald-200";
  return "text-zinc-700 bg-zinc-50 border-zinc-200";
}

function FyChip({ active, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-2 py-0.5 rounded border text-[10.5px] uppercase tracking-wider font-bold transition ${
        active
          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
          : "bg-white text-zinc-700 border-zinc-300 hover:border-indigo-400 hover:text-indigo-700"
      }`}
    >
      {label}
    </button>
  );
}

// DialogOccupancyOverlay — single-property or cluster weighted occupancy line over the
// dialog's 12-month stacked bar trend. Mirrors the company-level overlay in
// MisCategoryExpenses but uses the dialog's `rows` shape (key/total/occ_pct/is_rollup_only).
function DialogOccupancyOverlay({ rows }) {
  if (!rows?.length) return null;
  const hasOcc = rows.some((r) => r.occ_pct != null);
  if (!hasOcc) return null;
  // Overlay sits INSIDE the bar zone (84px tall, items-end). bars max 80px tall.
  // Position the SVG to cover the bar area exactly: bottom 0, height 80px.
  const barAreaHeight = 80;
  const n = rows.length;
  const xPct = (i) => ((i + 0.5) / n) * 100;
  const yPct = (occ) => 100 - Math.min(100, Math.max(0, occ));
  const linePoints = rows
    .map((r, i) => (r.occ_pct == null ? null : `${xPct(i)},${yPct(r.occ_pct)}`))
    .filter(Boolean)
    .join(" ");
  return (
    <div className="absolute pointer-events-none" style={{ left: 0, right: 0, bottom: 0, height: barAreaHeight }} data-testid="members-dialog-occ-overlay">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <line x1="0" y1="50" x2="100" y2="50" stroke="#312e81" strokeOpacity="0.08" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        <line x1="0" y1="25" x2="100" y2="25" stroke="#312e81" strokeOpacity="0.06" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        <line x1="0" y1="75" x2="100" y2="75" stroke="#312e81" strokeOpacity="0.06" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        <polyline
          points={linePoints}
          fill="none"
          stroke="#0f766e"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {rows.map((r, i) =>
        r.occ_pct == null ? null : (
          <div
            key={r.key}
            data-testid={`members-dialog-occ-dot-${i}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: `${xPct(i)}%`, top: `${yPct(r.occ_pct)}%` }}
            title={`${r.month_label} occupancy: ${r.occ_pct}%`}
          >
            <span className="w-2 h-2 rounded-full bg-teal-700 ring-2 ring-white shadow-sm" />
            <span className="text-[8.5px] tabular-nums font-bold text-teal-800 bg-white/85 backdrop-blur-sm rounded px-0.5 mt-0.5 leading-tight">{r.occ_pct}%</span>
          </div>
        )
      )}
    </div>
  );
}


// CommBenchmarkStripDialog — shown above the line-item history when Others is opened.
// Per BD spec: Communication tracking started Oct/Nov 2025, so we ONLY show the latest-month
// variance (aggregating across legacy months produces a misleading −100%).
function CommBenchmarkStripDialog({ meta, rows }) {
  if (!meta) return null;
  // Use the latest row (rows are ordered oldest→newest in the API response).
  const latest = rows.length ? rows[rows.length - 1] : null;
  const actual = latest?.comm_actual ?? 0;
  const benchmark = latest?.comm_benchmark ?? 0;
  const variance = actual - benchmark;
  const variancePct = latest?.comm_variance_pct ?? (benchmark > 0 ? Number(((actual - benchmark) / benchmark * 100).toFixed(1)) : null);
  const tolerance = meta.tolerance_pct ?? 15;
  const tone =
    variancePct == null ? "border-zinc-200 text-zinc-700 bg-zinc-50" :
    variancePct > tolerance ? "border-rose-300 text-rose-800 bg-rose-50" :
    variancePct > 5 ? "border-amber-300 text-amber-800 bg-amber-50" :
    variancePct < -tolerance ? "border-zinc-200 text-zinc-700 bg-zinc-50" :
    "border-emerald-300 text-emerald-800 bg-emerald-50";
  return (
    <section className={`border-2 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap ${tone}`} data-testid="members-dialog-comm-strip">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 flex items-center gap-1.5">
          <Zap className="w-3 h-3" /> Communication cost benchmark · ₹{meta.per_bed_per_day_inr}/bed/day
        </div>
        <div className="text-[11px] mt-0.5">
          Expected = capacity beds × days in month × ₹{meta.per_bed_per_day_inr}. Latest month only · tracking started late 2025.
        </div>
      </div>
      <div className="text-right">
        <div className="text-[9.5px] uppercase tracking-wider font-bold opacity-80">
          {latest?.month_label || "—"} · Variance
        </div>
        <div className="font-display font-black tabular-nums" style={{ fontSize: 18, lineHeight: 1.4 }}>
          {variancePct == null ? "—" : `${variancePct > 0 ? "+" : ""}${variancePct}%`}
        </div>
        <div className="text-[10.5px] tabular-nums opacity-80">
          {fmtL(actual)} vs {fmtL(benchmark)} · {variance >= 0 ? "+" : "−"}{fmtL(Math.abs(variance))}
        </div>
      </div>
    </section>
  );
}


export { MEMBER_COLOR, MEMBER_ICON };
